import React from "react";
import { Link } from "react-router";

import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";

import { enrichmentStatusColumn } from "@/components/common/status-columns";
import { EditButton } from "@/components/refine-ui/buttons/edit";
import { ShowButton } from "@/components/refine-ui/buttons/show";
import { Badge } from "@/components/ui/badge";
import { makeTooltip } from "@/components/ui/tooltip";
import { parseUnixTimestamp } from "@/lib/dateUtils";
import { IScreeningData } from "@/types/screening";

/**
 * Screening dates come back from the Admin explore endpoint as ISO strings
 * (DateTimeOffset serialized on ScreeningSummary), not unix seconds — so parse
 * accordingly. A numeric fallback keeps it working if the raw value is ever a
 * unix timestamp.
 */
function formatDate(raw: string | number | undefined): string {
  if (raw === undefined || raw === null || raw === "") return "—";
  const d = typeof raw === "number" ? parseUnixTimestamp(raw) : dayjs(raw);
  return d.isValid() ? d.format("DD/MM/YYYY HH:mm") : "—";
}

export const useScreeningColumns = () =>
  React.useMemo<ColumnDef<IScreeningData>[]>(
    () => [
      { id: "id", accessorKey: "document.id", header: "ID" },
      {
        // id doubles as the Typesense filter field for text filters.
        id: "movie.title",
        header: "Movie",
        minSize: 200,
        size: 260,
        accessorFn: (row) => row.document.movie?.title ?? "—",
        enableSorting: false,
        cell: function render({ row, getValue }: any) {
          return makeTooltip(
            <Link
              to={`/screenings/show/${row.original.document.id}`}
              className="block truncate font-medium hover:underline"
            >
              {getValue()}
            </Link>,
          );
        },
        meta: { filterType: "text" },
      },
      {
        id: "venue.name",
        header: "Venue",
        size: 200,
        accessorFn: (row) => row.document.venue?.name ?? "—",
        enableSorting: false,
        cell: function render({ getValue }: any) {
          return makeTooltip(<div className="truncate">{getValue()}</div>);
        },
        meta: { filterType: "text" },
      },
      {
        id: "startDate",
        header: "Starts",
        size: 150,
        accessorFn: (row) => row.document.startDate,
        // `startDate` is a numeric sort field in the screenings Typesense
        // collection (the BFF sorts on it), so column id → sort field works.
        enableSorting: true,
        cell: function render({ getValue }: any) {
          return makeTooltip(formatDate(getValue()));
        },
        meta: { filterType: "date" },
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "document.status",
        enableSorting: false,
        size: 120,
        cell: function render({ getValue }: any) {
          const status = getValue();
          return status ? <Badge variant="default">{status}</Badge> : null;
        },
        meta: {
          filterType: "select",
          options: [
            { label: "Draft", value: "Draft" },
            { label: "Complete", value: "Complete" },
            { label: "Cancelled", value: "Cancelled" },
          ],
        },
      },
      // Immediately after Status — the shared definition. A screening is
      // NOT embeddable, so there is no embedding column.
      enrichmentStatusColumn<IScreeningData>(),
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enablePinning: true,
        size: 100,
        cell: function render({ row }) {
          return (
            <div className="flex items-center gap-1">
              <ShowButton
                size="sm"
                hideText
                recordItemId={row.original.document.id}
              />
              <EditButton
                size="sm"
                hideText={true}
                recordItemId={row.original.document.id}
              />
            </div>
          );
        },
      },
    ],
    [],
  );
